// Нов доставчик направо от документа „Доставка".
//
// Полетата са СЪЩИТЕ три като в екрана „Доставчици" на организацията (име, ЕИК,
// ДДС номер) — записът отива в същата таблица на org базата, затова тук няма
// нито поле повече, нито по-слаба валидация. ЕИК-ът се проверява с контролна
// цифра, а уникалността на ЕИК/ДДС номер я пази бекендът (409).
//
// Шелът е общият (cloudts-design/references/modals.md), клас „Default";
// Esc и клик на фона ги поема antd (`onCancel`) — без custom handler.
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { App, Input, Modal } from "antd";
import { CloseOutlined, ShopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Supplier } from "../../../../api/inventory";
import { useCreateSupplier } from "../../../../hooks/useInventory";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";
import {
  isValidBgVat,
  isValidEIK,
  normalizeVatInput,
} from "../../../../lib/companyValidators";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (supplier: Supplier) => void;
};

type FieldErrors = {
  name?: string;
  eik?: string;
  vatNumber?: string;
};

export const SupplierCreateModal = ({ open, onClose, onCreated }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const createSupplier = useCreateSupplier();
  const [name, setName] = useState("");
  const [eik, setEik] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    if (!open) {
      setName("");
      setEik("");
      setVatNumber("");
      setErrors({});
    }
  }, [open]);

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = t("inventoryPage.supplierNameRequired");
    if (eik.trim() && !isValidEIK(eik)) {
      next.eik = t("inventoryPage.supplierEikInvalid");
    }
    const vat = normalizeVatInput(vatNumber);
    if (vat && !isValidBgVat(vat)) {
      next.vatNumber = t("inventoryPage.supplierVatNumberInvalid");
    }
    return next;
  };

  const handleSave = async () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      const supplier = await createSupplier.mutateAsync({
        name: name.trim(),
        eik: eik.trim() || null,
        vatNumber: normalizeVatInput(vatNumber) || null,
      });
      message.success(t("inventoryPage.supplierSaved"));
      onCreated(supplier);
      onClose();
    } catch (error) {
      // 409 от бекенда (дублиран ЕИК / ДДС номер) носи готов текст в `detail`.
      // Проверката за низ пази от pydantic 422, където `detail` е СПИСЪК от
      // обекти — подаден на toast-а, той би гръмнал при рендер.
      const detail = (error as { response?: { data?: { detail?: unknown } } })
        .response?.data?.detail;
      message.error(typeof detail === "string" ? detail : t("common.error"));
    }
  };

  const width = isMobile ? undefined : isTablet ? 420 : 460;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="bg-surface text-ink">
        <div
          className={`flex items-start gap-3 border-b border-shade-2 ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-5"
          }`}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint">
            <ShopOutlined className="text-[20px] text-brand-deep-ink" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[18px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {t("inventoryPage.supplierAddTitle")}
            </h2>
            <p className="m-0 mt-1 text-[13px] font-medium leading-[1.4] text-body">
              {t("inventoryPage.supplierAddHint")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
          >
            <CloseOutlined className="text-[14px]" />
          </button>
        </div>

        <div
          className={`flex flex-col gap-4 ${isNarrow ? "p-4" : "px-6 pb-6 pt-5"}`}
        >
          <Field label={t("inventoryPage.supplierName")} error={errors.name}>
            <Input
              value={name}
              autoFocus
              maxLength={255}
              placeholder={t("inventoryPage.supplierNamePh")}
              status={errors.name ? "error" : undefined}
              onChange={(e) => setName(e.target.value)}
              onPressEnter={handleSave}
              className="!h-10"
            />
          </Field>

          <Field label={t("inventoryPage.supplierEik")} error={errors.eik}>
            <Input
              value={eik}
              maxLength={32}
              placeholder={t("inventoryPage.supplierEikPh")}
              status={errors.eik ? "error" : undefined}
              onChange={(e) => setEik(e.target.value)}
              onPressEnter={handleSave}
              className="!h-10"
            />
          </Field>

          <Field
            label={t("inventoryPage.supplierVatNumber")}
            error={errors.vatNumber}
            hint={t("inventoryPage.supplierVatNumberHint")}
          >
            <Input
              value={vatNumber}
              maxLength={32}
              placeholder={t("inventoryPage.supplierVatNumberPh")}
              status={errors.vatNumber ? "error" : undefined}
              onChange={(e) => setVatNumber(e.target.value)}
              onPressEnter={handleSave}
              className="!h-10"
            />
          </Field>

          <div
            className={`flex gap-2 ${
              isMobile ? "flex-col-reverse" : "justify-end"
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`h-10 rounded-[10px] border border-shade-2 bg-transparent px-4 text-[14px] font-semibold text-body transition-colors duration-150 hover:bg-shade-1 ${
                isMobile ? "w-full" : ""
              }`}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={createSupplier.isPending}
              className={`h-10 rounded-[10px] border-none bg-brand px-4 text-[14px] font-semibold text-white transition-colors duration-150 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-60 ${
                isMobile ? "w-full" : ""
              }`}
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 text-[10.5px] font-bold uppercase leading-[normal] tracking-[1.2px] text-muted">
        {label}
      </div>
      {children}
      {error ? (
        <div className="mt-1 text-[11px] leading-snug text-danger">{error}</div>
      ) : hint ? (
        <div className="mt-1 text-[11px] leading-snug text-muted">{hint}</div>
      ) : null}
    </div>
  );
}
